import type { DeliveryZone } from '@/types/zones';
import { NotFoundError } from '../errors';

// Mock data for delivery zones
const mockZones: DeliveryZone[] = [
  {
    id: 'zone-1',
    name: 'Port of Miami',
    region: 'Southeast',
    country: 'USA',
    type: 'port',
    address: '1015 N America Way, Miami, FL 33132',
    coordinates: {
      lat: 25.7751,
      lng: -80.1706 
    },
    isActive: true,
    deliveryDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    leadTimeDays: 2,
    minimumOrderValue: 500,
    deliveryFee: 0,
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-06-20T14:30:00Z',
  },
  {
    id: 'zone-2',
    name: 'Port Everglades',
    region: 'Southeast',
    country: 'USA',
    type: 'port',
    address: '1850 Eller Dr, Fort Lauderdale, FL 33316',
    coordinates: {
      lat: 26.0893,
      lng: -80.1178 
    },
    isActive: true,
    deliveryDays: ['monday', 'wednesday', 'friday'],
    leadTimeDays: 2,
    minimumOrderValue: 750,
    deliveryFee: 50,
    createdAt: '2024-02-10T09:00:00Z',
    updatedAt: '2024-05-15T11:00:00Z',
  },
  {
    id: 'zone-3',
    name: 'Port Canaveral',
    region: 'Southeast',
    country: 'USA',
    type: 'port',
    address: '445 Challenger Rd, Cape Canaveral, FL 32920',
    coordinates: {
      lat: 28.4089,
      lng: -80.6100 
    },
    isActive: true,
    deliveryDays: ['tuesday', 'thursday'],
    leadTimeDays: 3,
    minimumOrderValue: 1000,
    deliveryFee: 75,
    createdAt: '2024-03-05T08:00:00Z',
    updatedAt: '2024-04-22T16:45:00Z',
  },
  {
    id: 'zone-4',
    name: 'Barcelona Port',
    region: 'Mediterranean',
    country: 'Spain',
    type: 'port',
    address: 'Moll de Barcelona, 08039 Barcelona',
    coordinates: {
      lat: 41.3751,
      lng: 2.1797 
    },
    isActive: false,
    deliveryDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    leadTimeDays: 5,
    minimumOrderValue: 2000,
    deliveryFee: 150,
    notes: 'Seasonal service - active April through October',
    createdAt: '2024-01-20T12:00:00Z',
    updatedAt: '2024-03-10T09:30:00Z',
  },
  {
    id: 'zone-5',
    name: 'LAX Cargo',
    region: 'West Coast',
    country: 'USA',
    type: 'airport',
    address: '6000 Avion Dr, Los Angeles, CA 90045',
    coordinates: {
      lat: 33.9416,
      lng: -118.4085 
    },
    isActive: true,
    deliveryDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
    leadTimeDays: 1,
    minimumOrderValue: 300,
    deliveryFee: 25,
    createdAt: '2024-04-01T10:00:00Z',
    updatedAt: '2024-06-18T13:15:00Z',
  },
  {
    id: 'zone-6',
    name: 'Chicago Distribution Hub',
    region: 'Midwest',
    country: 'USA',
    type: 'distribution_center',
    address: '2100 Clearwater Dr, Oak Brook, IL 60523',
    coordinates: {
      lat: 41.8500,
      lng: -87.9500 
    },
    isActive: true,
    deliveryDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    leadTimeDays: 2,
    minimumOrderValue: 400,
    deliveryFee: 35,
    createdAt: '2024-02-28T11:00:00Z',
    updatedAt: '2024-06-01T10:00:00Z',
  },
];

// Simulated API delay
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export async function getDeliveryZones(): Promise<DeliveryZone[]> {
  await delay(500);
  return mockZones;
}

export async function getDeliveryZone(id: string): Promise<DeliveryZone | null> {
  await delay(300);
  return mockZones.find((zone) => zone.id === id) ?? null;
}

export async function updateDeliveryZone(
  id: string,
  updates: Partial<DeliveryZone>
): Promise<DeliveryZone> {
  await delay(400);
  const zone = mockZones.find((z) => z.id === id);
  if (!zone) {
    throw new NotFoundError('Zone');
  }
  return {
    ...zone,
    ...updates,
    updatedAt: new Date().toISOString() 
  };
}

export async function toggleZoneStatus(id: string): Promise<DeliveryZone> {
  await delay(300);
  const zone = mockZones.find((z) => z.id === id);
  if (!zone) {
    throw new NotFoundError('Zone');
  }
  return {
    ...zone,
    isActive: !zone.isActive,
    updatedAt: new Date().toISOString() 
  };
}
